import React, { useEffect, useState, useMemo } from "react";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Button, Grid, TextField, MenuItem } from "@material-ui/core";
import { Panel } from "primereact/panel";
import { NotificationManager } from "react-notifications";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import GeneralService from "../apis/GeneralService";
import IconButton from "@material-ui/core/IconButton";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import CodeIcon from "@material-ui/icons/Code";
import DescriptionIcon from "@material-ui/icons/Description";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: "#3C8DBC",
        color: theme.palette.common.white,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
    root: {
        "&:nth-of-type(odd)": {
            backgroundColor: "rgba(0,0,0,0.04)",
        },
    },
}))(TableRow);

const useStyles = makeStyles({
    table: { minWidth: 700 },
    searchContainer: { padding: "20px" },
    searchField: { marginBottom: "15px" },
    infoBox: {
        padding: "10px 12px",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        marginBottom: 10,
        background: "#fafafa",
    },
    infoLabel: { fontWeight: 600, marginRight: 6 },
    filterCell: {
        padding: "8px 16px",
        backgroundColor: "#f5f5f5",
    },
});

export default function ConsultaComprobante() {
    const classes = useStyles();

    const [tipoComprobante, setTipoComprobante] = useState("");
    const [identificacion, setIdentificacion] = useState("");
    const [numeroCuenta, setNumeroCuenta] = useState("");
    const [numeroTitulo, setNumeroTitulo] = useState("");
    const [rangoFecha, setRangoFecha] = useState("TODOS"); // Valor por defecto
    const [showTable, setShowTable] = useState(false);
    const [data, setData] = useState([]);
    const [filtroPeriodo, setFiltroPeriodo] = useState("");

    // Info contribuyente (encima de la tabla)
    const [contribuyente, setContribuyente] = useState({
        identificacion: "",
        nombre: "",
    });

    // Modal HTML Título
    const [openTitulo, setOpenTitulo] = useState(false);
    const [tituloHtml, setTituloHtml] = useState("");

    // Filtrar datos por periodo
    const dataFiltrada = useMemo(() => {
        if (!filtroPeriodo || filtroPeriodo.trim() === "") {
            return data;
        }
        return data.filter((row) => {
            const periodo = (row.periodo || "").toLowerCase();
            return periodo.includes(filtroPeriodo.toLowerCase());
        });
    }, [data, filtroPeriodo]);

    // Función para calcular fecha desde según el rango seleccionado
    const calcularFechaDesde = (rango) => {
        // Si es TODOS, retornar null
        if (rango === "TODOS") {
            return null;
        }

        const ahora = new Date();
        let fechaDesde = new Date();

        switch (rango) {
            case "DIA_ACTUAL":
                fechaDesde.setHours(0, 0, 0, 0);
                break;
            case "SEMANAL":
                fechaDesde.setDate(ahora.getDate() - 7);
                break;
            case "MENSUAL":
                fechaDesde.setMonth(ahora.getMonth() - 1);
                break;
            case "2_MESES":
                fechaDesde.setMonth(ahora.getMonth() - 2);
                break;
            case "3_MESES":
                fechaDesde.setMonth(ahora.getMonth() - 3);
                break;
            default:
                return null; // Por defecto null
        }

        return fechaDesde;
    };

    const handleSearch = () => {
        // Validar tipo de comprobante
        if (!tipoComprobante || tipoComprobante.trim() === "") {
            NotificationManager.warning(
                "Debe seleccionar un tipo de comprobante",
                "",
                3000
            );
            return;
        }

        // Validar criterios según tipo
        if (tipoComprobante === "AGUA") {
            if (!identificacion || identificacion.trim() === "" ) {
                NotificationManager.warning(
                    "Debe ingresar la identificación del contribuyente",
                    "",
                    3000
                );
                return;
            }
        } else {
            // Para OTROS solo identificacion o numeroTitulo
            if (!identificacion || identificacion.trim() === "") {
                NotificationManager.warning(
                    "Debe ingresar la identificación del contribuyente",
                    "",
                    3000
                );
                return;
            }
        }

        setShowTable(false);
        setContribuyente({ identificacion: "", nombre: "" });
        setFiltroPeriodo(""); // Limpiar filtro al buscar

        if (!GeneralService.api_url) {
            NotificationManager.warning(
                "Configuración cargando... intenta de nuevo en unos segundos.",
                "",
                3000
            );
            return;
        }

        // Calcular fechaDesde según el rango seleccionado
        const fechaDesde = calcularFechaDesde(rangoFecha);

        // Preparar parámetros según tipo
        let searchParams = {};
        let serviceCall;

        if (tipoComprobante === "AGUA") {
            searchParams = {
                identificacion: identificacion.trim(),
                numeroCuenta: numeroCuenta.trim(),
                numeroTitulo: numeroTitulo.trim(),
                fechaDesde: fechaDesde ? fechaDesde.getTime() : null, // null si es TODOS
            };
            serviceCall = GeneralService.buscarComprobantes(searchParams);
        } else {
            searchParams = {
                identificacion: identificacion.trim(),
                numeroTitulo: numeroTitulo.trim(),
                fechaDesde: fechaDesde ? fechaDesde.getTime() : null, // null si es TODOS
            };
            serviceCall = GeneralService.buscarOtrosComprobantes(searchParams);
        }

        serviceCall
            .then((result) => {
                const comprobantes = Array.isArray(result.data) ? result.data : [];
                setData(comprobantes);
                setShowTable(true);

                NotificationManager.success(
                    `Se encontraron ${comprobantes.length} registro(s)`,
                    "",
                    3000
                );
            })
            .catch((err) => {
                const status = err?.response?.status;
                const msg = err?.response?.data;

                if (status === 400) {
                    NotificationManager.warning(msg || "Datos inválidos.", "", 3000);
                    setData([]);
                    setShowTable(false);
                    return;
                }

                if (status === 404) {
                    NotificationManager.warning(
                        msg || "No se encontraron resultados.",
                        "",
                        3000
                    );
                    setData([]);
                    setShowTable(false);
                    return;
                }

                NotificationManager.error(
                    msg || "Error en consulta, intente más tarde.",
                    "",
                    5000
                );
            });
    };

    const handleClear = () => {
        setTipoComprobante("");
        setIdentificacion("");
        setNumeroCuenta("");
        setNumeroTitulo("");
        setRangoFecha("TODOS"); // Resetear a valor por defecto
        setShowTable(false);
        setData([]);
        setContribuyente({ identificacion: "", nombre: "" });
        setFiltroPeriodo(""); // Limpiar filtro
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const wrapHtml = (html) => {
        const css = `
    <style>
      * { box-sizing: border-box; }

      html, body { margin: 0; padding: 0; width: 100%; }

      /* margen interno a los lados (y un poquito arriba/abajo) */
      body { padding: 12px 16px; background: #fff; }

      table { width: 100% !important; }
      img { max-width: 100% !important; height: auto !important; }

      /* también aplica en impresión */
      @media print {
        body { padding: 12px 16px; }
      }
    </style>
  `;

        if (/<html[\s>]/i.test(html)) {
            if (/<head[\s>]/i.test(html)) {
                return html.replace(/<head[^>]*>/i, (m) => `${m}${css}`);
            }
            return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${css}</head>`);
        }

        return `<!doctype html><html><head><meta charset="utf-8">${css}</head><body>${html}</body></html>`;
    };

    const imprimirHtml = (html) => {
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(wrapHtml(html));
        doc.close();

        iframe.onload = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => document.body.removeChild(iframe), 500);
        };
    };

    const handleDownloadPdf = async (row) => {
        try {
            const res = await GeneralService.descargarComprobantePdf({
                comId: row.comId,
            });
            downloadBlob(
                res.data,
                `comprobante_${row.comSecuencial || "sin_numero"}.pdf`
            );
        } catch (e) {
            NotificationManager.error("No se pudo descargar el PDF.", "", 4000);
        }
    };

    const handleDownloadXml = async (row) => {
        try {
            const res = await GeneralService.descargarComprobanteXml({
                comId: row.comId,
            });
            const nombre = `${row.trcContribuyenteNombre || "comprobante"}_${
                row.comSecuencial || ""
            }.xml`;
            downloadBlob(res.data, nombre);
        } catch (e) {
            NotificationManager.error("No se pudo descargar el XML.", "", 4000);
        }
    };

    const handleDownloadTitulo = async (row) => {
        try {
            const trcId = row.trcId;
            const res = await GeneralService.descargarTituloHtml({ trcId });
            setTituloHtml(res.data);
            setOpenTitulo(true);
        } catch (e) {
            NotificationManager.error(
                "No se pudo obtener el HTML del Título.",
                "",
                4000
            );
        }
    };

    const formatFecha = (value) => {
        if (value == null) return "";

        const n = typeof value === "string" ? Number(value) : value;

        if (!Number.isFinite(n)) {
            const d = new Date(value);
            return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("es-EC");
        }

        const ms = n < 1e12 ? n * 1000 : n;
        return new Date(ms).toLocaleDateString("es-EC");
    };

    // Contribuyente desde el primer registro
    useEffect(() => {
        if (!data || data.length === 0) {
            setContribuyente({ identificacion: "", nombre: "" });
            return;
        }

        const first = data[0] || {};
        const id = first?.trcContribuyenteId || identificacion || "";
        const nombre = first?.trcContribuyenteNombre || "";

        setContribuyente({
            identificacion: String(id || ""),
            nombre: String(nombre || ""),
        });
    }, [data, identificacion]);

    // Determinar si es tipo Agua
    const isAgua = tipoComprobante === "AGUA";

    return (
        <React.Fragment>
            <Panel header="CONSULTA DE COMPROBANTES ELECTRÓNICOS">
                <div className={classes.searchContainer}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Tipo de tributo *"
                                fullWidth
                                variant="outlined"
                                value={tipoComprobante}
                                onChange={(e) => {
                                    setTipoComprobante(e.target.value);
                                    // Limpiar campos al cambiar tipo
                                    setNumeroCuenta("");
                                    setShowTable(false);
                                    setData([]);
                                }}
                                className={classes.searchField}
                                required
                            >
                                <MenuItem value="AGUA">Agua</MenuItem>
                                <MenuItem value="OTROS">Otros Tributos</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Rango de Fecha *"
                                fullWidth
                                variant="outlined"
                                value={rangoFecha}
                                onChange={(e) => setRangoFecha(e.target.value)}
                                className={classes.searchField}
                                disabled={!tipoComprobante}
                            >
                                <MenuItem value="TODOS">Todos (Predeterminado)</MenuItem>
                                <MenuItem value="DIA_ACTUAL">Día Actual</MenuItem>
                                <MenuItem value="SEMANAL">Última Semana</MenuItem>
                                <MenuItem value="MENSUAL">Último Mes</MenuItem>
                                <MenuItem value="2_MESES">Últimos 2 Meses</MenuItem>
                                <MenuItem value="3_MESES">Últimos 3 Meses</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={isAgua ? 4 : 6}>
                            <TextField
                                label="Cédula / RUC"
                                fullWidth
                                variant="outlined"
                                value={identificacion}
                                onChange={(e) => setIdentificacion(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                                className={classes.searchField}
                                placeholder="Ej: 1234567890"
                                disabled={!tipoComprobante}
                                required
                            />
                        </Grid>

                        {isAgua && (
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Número de Cuenta"
                                    fullWidth
                                    variant="outlined"
                                    value={numeroCuenta}
                                    onChange={(e) => setNumeroCuenta(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSearch();
                                    }}
                                    className={classes.searchField}
                                    placeholder="Ej: 0323000016"
                                    disabled={!tipoComprobante}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} sm={isAgua ? 4 : 6}>
                            <TextField
                                label="Número de Título"
                                fullWidth
                                variant="outlined"
                                value={numeroTitulo}
                                onChange={(e) => setNumeroTitulo(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                                className={classes.searchField}
                                placeholder="Ej: 000123-2025"
                                disabled={!tipoComprobante}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} justifyContent="flex-end">
                        <Grid item>
                            <Button variant="outlined" color="default" onClick={handleClear}>
                                Limpiar
                            </Button>
                        </Grid>

                        <Grid item>
                            <Tooltip title="Buscar">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSearch}
                                    startIcon={<SearchIcon />}
                                    disabled={!tipoComprobante}
                                >
                                    Buscar
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </div>
            </Panel>

            <br />

            {showTable && (
                <Panel header={`Resultados: ${dataFiltrada.length} de ${data.length} registro(s)`}>
                    <div className={classes.infoBox}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <span className={classes.infoLabel}>Identificación:</span>
                                <span>{contribuyente.identificacion || identificacion}</span>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <span className={classes.infoLabel}>Nombre:</span>
                                <span>{contribuyente.nombre || "-"}</span>
                            </Grid>
                        </Grid>
                    </div>

                    <TableContainer component={Paper}>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>№ de Factura</StyledTableCell>
                                    <StyledTableCell>№ de Comprobante</StyledTableCell>
                                    <StyledTableCell>Estado</StyledTableCell>
                                    {isAgua && (
                                        <>
                                            <StyledTableCell>№ de Medidor</StyledTableCell>
                                            <StyledTableCell>№ de Cuenta</StyledTableCell>
                                        </>
                                    )}
                                    <StyledTableCell>Periodo</StyledTableCell>
                                    <StyledTableCell>Fecha de Recaudación</StyledTableCell>
                                    <StyledTableCell>Fecha de Emisión</StyledTableCell>
                                    <StyledTableCell>Fecha de Vencimiento</StyledTableCell>
                                    <StyledTableCell>Total</StyledTableCell>
                                    <StyledTableCell>Documentos</StyledTableCell>
                                </TableRow>
                                <TableRow>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    {isAgua && (
                                        <>
                                            <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                            <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                        </>
                                    )}
                                    <StyledTableCell className={classes.filterCell}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar periodo..."
                                            value={filtroPeriodo}
                                            onChange={(e) => setFiltroPeriodo(e.target.value)}
                                            variant="outlined"
                                            style={{ backgroundColor: "white" }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                    <StyledTableCell className={classes.filterCell}></StyledTableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {dataFiltrada.map((row, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell>{row.comSecuencial}</StyledTableCell>
                                        <StyledTableCell>{row.trcNumeroTitulo}</StyledTableCell>
                                        <StyledTableCell>{row.comEstado}</StyledTableCell>
                                        {isAgua && (
                                            <>
                                                <StyledTableCell>
                                                    {row.medNumeroMedidor || "N/D"}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {row.numeroCuenta || "N/D"}
                                                </StyledTableCell>
                                            </>
                                        )}
                                        <StyledTableCell>{row.periodo || "N/D"}</StyledTableCell>
                                        <StyledTableCell>
                                            {formatFecha(row.trcFechaRecaudacion)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {formatFecha(row.comFechaEmision)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {formatFecha(row.trcFechaVencimiento)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            ${row.trcTotalCobro?.toFixed(2) || "0.00"}
                                        </StyledTableCell>

                                        <StyledTableCell align="center">
                                            {row.comEstado === 'AUTORIZADO' && (
                                                <>
                                                    <Tooltip title="Descargar PDF">
                                                        <IconButton
                                                            onClick={() => handleDownloadPdf(row)}
                                                            size="small"
                                                        >
                                                            <PictureAsPdfIcon />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Descargar XML">
                                                        <IconButton
                                                            onClick={() => handleDownloadXml(row)}
                                                            size="small"
                                                        >
                                                            <CodeIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}

                                            <Tooltip title="Ver / Imprimir Título (HTML)">
                                                <IconButton
                                                    onClick={() => handleDownloadTitulo(row)}
                                                    size="small"
                                                >
                                                    <DescriptionIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Panel>
            )}

            <Dialog
                open={openTitulo}
                onClose={() => setOpenTitulo(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Vista previa del Título</DialogTitle>

                <DialogContent dividers style={{ padding: 0 }}>
                    <iframe
                        title="titulo-preview"
                        style={{
                            width: "100%",
                            height: "70vh",
                            border: 0,
                            display: "block",
                            background: "#fff",
                        }}
                        srcDoc={wrapHtml(tituloHtml)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenTitulo(false)} color="default">
                        Cerrar
                    </Button>

                    <Button
                        onClick={() => imprimirHtml(tituloHtml)}
                        color="primary"
                        variant="contained"
                    >
                        Imprimir
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}